<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
import type { SiteBlock, SiteColumn, SiteColumnsRatio } from '../../../types/site'

/**
 * Editor form for the columns block: ratio, gap, vertical alignment and
 * mobile stacking. Changing the ratio resizes `cols` in place — new columns
 * start empty, and when the count shrinks the trailing columns' blocks are
 * appended to the last surviving column rather than lost.
 */
const props = defineProps<{ block: SiteBlock }>()

const t = useSiteEditorT()

const RATIOS: ReadonlyArray<{ value: SiteColumnsRatio, label: string }> = [
  { value: '1-1', label: t('site.f.columns.opt_', '½ · ½') },
  { value: '1-2', label: t('site.f.columns.opt__2', '⅓ · ⅔') },
  { value: '2-1', label: t('site.f.columns.opt__3', '⅔ · ⅓') },
  { value: '1-1-1', label: t('site.f.columns.opt__4', '⅓ · ⅓ · ⅓') },
  { value: '1-1-1-1', label: t('site.f.columns.opt_4', '¼ × 4') }
]
const GAPS = [
  { value: 's', label: t('site.f.columns.opt_s', 'S') },
  { value: 'm', label: t('site.f.columns.opt_m', 'M') },
  { value: 'l', label: t('site.f.columns.opt_l', 'L') }
] as const
const ALIGNS = [
  { value: 'top', label: t('site.f.columns.opt_top', 'Top') },
  { value: 'center', label: t('site.f.columns.opt_center', 'Center') },
  { value: 'stretch', label: t('site.f.columns.opt_stretch', 'Stretch') }
] as const

const setRatio = (ratio: SiteColumnsRatio) => {
  const want = ratio.split('-').length
  const cols: SiteColumn[] = [...(props.block.data.cols ?? [])]
  while (cols.length < want) cols.push({ id: makeSiteBlockId(), blocks: [] })
  if (cols.length > want) {
    const keep = cols.slice(0, want)
    const overflow = cols.slice(want).flatMap(c => c.blocks)
    keep[want - 1]!.blocks.push(...overflow)
    props.block.data.cols = keep
  } else {
    props.block.data.cols = cols
  }
  props.block.data.ratio = ratio
}
</script>

<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <UFormField :label="t('site.f.common.columns_label', 'Columns')">
    <SiteEditorOptionToggle
      :options="RATIOS"
      :model-value="block.data.ratio ?? '1-1'"
      @update:model-value="setRatio($event as SiteColumnsRatio)"
    />
  </UFormField>
  <UFormField :label="t('site.f.columns.gap', 'Gap')">
    <SiteEditorOptionToggle
      :options="GAPS"
      :model-value="block.data.gap ?? 'm'"
      @update:model-value="block.data.gap = $event as SiteBlock['data']['gap']"
    />
  </UFormField>
  <UFormField :label="t('site.f.columns.vertical_alignment', 'Vertical alignment')">
    <SiteEditorOptionToggle
      :options="ALIGNS"
      :model-value="block.data.vertical_align ?? 'top'"
      @update:model-value="block.data.vertical_align = $event as SiteBlock['data']['vertical_align']"
    />
  </UFormField>
  <UFormField :label="t('site.f.columns.stack_on_mobile', 'Stack on mobile')">
    <USwitch
      :model-value="block.data.stack_on_mobile !== false"
      @update:model-value="block.data.stack_on_mobile = $event"
    />
  </UFormField>
  <p class="text-xs text-muted">
    Drag blocks from the palette into a column, or select a column's block to edit it.
  </p>
</template>
